import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { ModalComponent, ModalService, TileComponent } from 'mbs-ui-kit';
import type { ChangeFileSystemAccountModalComponent } from 'mbs-ui-shared@components/computers-modal/change-file-system-account-modal/change-file-system-account-modal.component';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { PermissionsEnum } from 'mbs-ui-shared@models';
import { UtilsPipeModule } from 'mbs-ui-shared@utils/utils.pipe.module';

@UntilDestroy()
@Component({
  standalone: true,
  imports: [CommonModule, UtilsPipeModule, HelpLinkDirective, ModalComponent, I18NextPipe, TileComponent, NgbTooltipModule],
  selector: 'mbs-destination-plus-retention-add-destination-modal',
  templateUrl: './add-destination-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DestinationPlusRetentionStepAddPlanModalComponent implements OnInit {
  private auth = inject(AuthFacade);
  private i18nPipe = inject(I18NextPipe);
  private modalService = inject(ModalService);
  private computersFacade = inject(ComputersFacade);
  public ability = inject(AbilityService);

  public readonly PermissionsEnum = PermissionsEnum;
  public readonly routingPath = RoutingPath;
  public readonly addNewDestinationDescription = {
    fileSystem: this.i18nPipe.transform('wizards:destinationPlusRetention.addNewDestination.description.fileSystem', {
      returnObjects: true
    }),
    localFileSystem: this.i18nPipe.transform('wizards:destinationPlusRetention.addNewDestination.description.localFileSystem', {
      returnObjects: true
    })
  };

  protected isUnix = false;
  protected isNetBuild = false;
  protected isReadOnlySuperAdmin = false;

  @ViewChild(ModalComponent, { static: true }) private baseModal;

  constructor() {
    this.auth.currentUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.isReadOnlySuperAdmin = user.IsSuperAdmin && user.IsReadOnly;
    });
  }

  ngOnInit() {
    this.isUnix = this.baseModal.data.isUnix;
    this.isNetBuild = this.baseModal.data.isNetBuild;
  }

  addFileSystemDestination(): void {
    const url = `${this.routingPath.ApStorages}/addNew`;

    window.open(url, '_blank');

    this.baseModal.close();
  }

  addLocalFileSystemDestination(): void {
    this.baseModal.loading = true;

    this.computersFacade
      .getByHid$(this.baseModal.data.hid)
      .pipe(untilDestroyed(this))
      .subscribe((computer) => {
        import('mbs-ui-shared@components/computers-modal/change-file-system-account-modal/change-file-system-account-modal.component').then(
          ({ ChangeFileSystemAccountModalComponent }) => {
            const modal = this.modalService.openRef(ChangeFileSystemAccountModalComponent);

            (modal.componentInstance as ChangeFileSystemAccountModalComponent).computer = computer;
          }
        );
      });

    this.baseModal.close();
  }
}
